/**
 * Workspace guide selection — exactly one of concierge / harry / wes is active.
 */

import { create } from 'zustand';
import {
  getAgentSettings,
  patchAgentSettingsEnabled,
  upsertAgentSettings,
  type AgentSettings,
} from '../../lib/agentSettingsService';
import type { GuideId } from '../../lib/guide/guideAgents';
import {
  GUIDE_AGENT_TYPE,
  inferGuideFromLegacy,
  packGuideSettings,
  unpackGuideSettings,
} from '../../lib/guide/guideAgentSettings';
import { HARRY_AGENT_TYPE } from './harryStore';
import { WES_AGENT_TYPE } from './wesStore';
import { useHarryStore } from './harryStore';
import { useWesStore } from './wesStore';
import { DEFAULT_MODEL } from '../../../supabase/functions/_shared/modelRequest.ts';

interface GuideAgentStoreState {
  activeGuide: GuideId;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
  loadedWorkspaceId: string | null;

  fetchActiveGuide: (workspaceId: string) => Promise<void>;
  setActiveGuide: (
    workspaceId: string,
    guideId: GuideId,
    updatedBy?: string,
  ) => Promise<{ success: boolean; error?: string }>;
}

function mergeEnabledFlag(current: AgentSettings | null, enabled: boolean) {
  if (!current) return current;
  return { ...current, enabled };
}

async function syncPersonaEnabledFlags(
  workspaceId: string,
  activeGuide: GuideId,
  updatedBy?: string,
): Promise<string | null> {
  const [harryResult, wesResult] = await Promise.all([
    patchAgentSettingsEnabled({
      workspaceId,
      agentType: HARRY_AGENT_TYPE,
      enabled: activeGuide === 'harry',
      updatedBy,
    }),
    patchAgentSettingsEnabled({
      workspaceId,
      agentType: WES_AGENT_TYPE,
      enabled: activeGuide === 'wes',
      updatedBy,
    }),
  ]);

  if (harryResult.error) return harryResult.error;
  if (wesResult.error) return wesResult.error;

  const harryConfig = useHarryStore.getState().config;
  const wesConfig = useWesStore.getState().config;

  if (harryResult.data) {
    useHarryStore.setState({
      config: harryConfig
        ? mergeEnabledFlag(harryConfig, harryResult.data.enabled)
        : harryResult.data,
    });
  }
  if (wesResult.data) {
    useWesStore.setState({
      config: wesConfig
        ? mergeEnabledFlag(wesConfig, wesResult.data.enabled)
        : wesResult.data,
    });
  }

  return null;
}

export const useGuideAgentStore = create<GuideAgentStoreState>((set, get) => ({
  activeGuide: 'concierge',
  isLoading: false,
  isSaving: false,
  error: null,
  loadedWorkspaceId: null,

  fetchActiveGuide: async (workspaceId) => {
    if (get().loadedWorkspaceId === workspaceId && !get().isLoading) {
      return;
    }

    set({ isLoading: true, error: null });

    const [{ data: guideRow, error: guideError }, { data: harryRow }, { data: wesRow }] =
      await Promise.all([
        getAgentSettings(workspaceId, GUIDE_AGENT_TYPE),
        getAgentSettings(workspaceId, HARRY_AGENT_TYPE),
        getAgentSettings(workspaceId, WES_AGENT_TYPE),
      ]);

    if (guideError) {
      set({ isLoading: false, error: guideError });
      return;
    }

    const activeGuide = guideRow
      ? unpackGuideSettings(guideRow.systemPrompt)
      : inferGuideFromLegacy(harryRow?.enabled ?? false, wesRow?.enabled ?? false);

    set({
      activeGuide,
      isLoading: false,
      loadedWorkspaceId: workspaceId,
    });
  },

  setActiveGuide: async (workspaceId, guideId, updatedBy) => {
    set({ isSaving: true, error: null });

    await Promise.all([
      useHarryStore.getState().config
        ? Promise.resolve()
        : useHarryStore.getState().fetchConfig(workspaceId, updatedBy),
      useWesStore.getState().config
        ? Promise.resolve()
        : useWesStore.getState().fetchConfig(workspaceId),
    ]);

    const { error: guideError } = await upsertAgentSettings({
      workspaceId,
      agentType: GUIDE_AGENT_TYPE,
      enabled: true,
      model: DEFAULT_MODEL,
      systemPrompt: packGuideSettings(guideId),
      autoRunOnCreate: false,
      updatedBy,
    });

    if (guideError) {
      set({ isSaving: false, error: guideError });
      return { success: false, error: guideError };
    }

    const syncError = await syncPersonaEnabledFlags(workspaceId, guideId, updatedBy);
    if (syncError) {
      set({ isSaving: false, error: syncError });
      return { success: false, error: syncError };
    }

    set({ activeGuide: guideId, isSaving: false, loadedWorkspaceId: workspaceId });
    return { success: true };
  },
}));
